import type { ReactNode } from 'react';
import { Mono } from '@jotpsych/ui';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Stack, Typography } from '@mui/material';

import type { ClaimWorkItem } from '../../../store';

import { HOVER_DETAIL_TRIGGER_SX, HoverDetail } from './HoverDetail';
import { ReasonDetail } from './ReasonDetail';
import { reasonCategoryLabel } from './reasonDetailModel';

export interface ReasonCellProps {
  item: ClaimWorkItem;
  outcome?: ReactNode;
  content?: ReactNode;
  onOpen?: () => void;
}

/**
 * Shared by the Claims reason and Home outcome cells. A click still opens the row.
 * Keep the trigger when the queue's reason is missing: the workspace may still
 * provide payer details, and the popover states explicitly when none are available.
 */
export const ReasonCell = ({ item, outcome, content, onOpen }: ReasonCellProps) => (
  <HoverDetail
    content={
      content ?? <ReasonDetail payerStatus={item.last_payer_status} reason={item.outcome_reason} />
    }
    maxWidth={420}
    onOpen={onOpen}
  >
    {trigger => (
      <Stack
        {...trigger}
        data-testid={`claim-reason-${item.claim_id}`}
        direction="row"
        spacing={1}
        sx={{ alignItems: 'center', minWidth: 0, ...HOVER_DETAIL_TRIGGER_SX }}
      >
        {outcome}
        <Box sx={{ minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
            <Typography
              component="div"
              sx={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                minWidth: 0,
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
              }}
            >
              {reasonCategoryLabel(item.outcome_reason?.category)}
            </Typography>
            <InfoOutlinedIcon sx={{ color: 'text.secondary', flexShrink: 0, fontSize: 14 }} />
          </Stack>
          {!!item.outcome_reason?.codes?.length && (
            <Mono
              color="text.secondary"
              component="div"
              noWrap
              sx={{ fontSize: '0.75rem', mt: 0.25 }}
            >
              {item.outcome_reason.codes.join(' · ')}
            </Mono>
          )}
        </Box>
      </Stack>
    )}
  </HoverDetail>
);
